import React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useBreakpoint } from '../useBreakpoint';
import { buttonReset, color, font, TAP, weight } from '../theme';
import Icon from './Icon';

interface BackLinkProps {
  // Where to go when the page was opened directly (no in-app history to return to)
  fallback: string;
  // Name of the fallback page, shown when there is no `from` label
  fallbackLabel: string;
  // Light text for use on a colored header band
  onDark?: boolean;
}

/* "› <previous page>" at the top of a detail page. Shows the caller's `from` label and
   goes back in history; opened from a direct link, it replaces the entry with the
   fallback page so the browser's Back button doesn't return here.
   Hidden on desktop, where the breadcrumbs and the browser's Back button do this. */
const BackLink = ({ fallback, fallbackLabel, onDark = false }: BackLinkProps) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { isDesktop } = useBreakpoint();
  if (isDesktop) return null;

  const from = (location.state as { from?: string } | null)?.from;
  const label = from || fallbackLabel;

  return (
    <button
      type="button"
      onClick={() => (from ? navigate(-1) : navigate(fallback, { replace: true }))}
      aria-label={`חזרה: ${label}`}
      style={{
        ...buttonReset,
        display: 'inline-flex',
        alignItems: 'center',
        gap: 2,
        minHeight: TAP,
        maxWidth: '100%',
        fontSize: font.label,
        fontWeight: weight.medium,
        color: onDark ? color.textOnPrimary : color.primary,
      }}
    >
      <Icon name="chevronBack" size={18} strokeWidth={2.2} />
      <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{label}</span>
    </button>
  );
};

export default BackLink;
